import apiEndPoints from 'common/apiEndPoints';
import { performRequest, performRequestSecond, performRequestThird } from 'common/apiHandler';
import { getTenantDetails } from 'utils/utils';

export const fetchFiles = (obj, exclude_from_loading = false) => {
  let {
    case_id = '',
    selectedFolder,
    selectedType,
    tag,
    is_read,
    query,
    pageLimit = 10,
    pagination_token = '',
    pagination_direction = 'forward',
    start_date,
    end_date,
    is_client_uploaded,
    esignature_status,
    shared_folder = false,
    shared_contact_list,
    folder_shared,
  } = obj;

  // Home breadcrumb logic: If no folder is selected, reset shared_folder to false
  if (!selectedFolder || selectedFolder === '' || selectedFolder === 'undefined') {
    shared_folder = false;
  }

  const [tenantId, tenantSk] = getTenantDetails();

  // Build base query parameters
  let queryParams = `tenant_id=${tenantId}&tenant_sk=${tenantSk}&limit=${pageLimit}&pagination_token=${pagination_token}&pagination_direction=${pagination_direction}`;

  // Check if current URL route contains /cases /client /leads /firm-documents and add request_type parameter
  if (window.location.hash.includes('/cases/')) {
    queryParams += `&request_type=case`;
  } else if (window.location.hash.includes('firm-documents')) {
    queryParams += `&request_type=firm_documents`;
  } else if (window.location.hash.includes('client/') || window.location.hash.includes('leads/')) {
    queryParams += `&request_type=client`;
  } else {
    queryParams += `&request_type=global`;
  }

  // Only add case_id if it exists in the object (not omitted)
  if (obj.hasOwnProperty('case_id')) {
    queryParams += `&case_id=${case_id ?? 'undefined'}`;
  }

  // Add other basic parameters
  queryParams += `&folder_id=${selectedFolder ?? ''}&format=${selectedType ?? ''}&query=${query || tag || ''}`;

  // Only add is_read if it's not undefined
  if (is_read !== undefined && is_read !== '') {
    queryParams += `&is_read=${is_read}`;
  }

  // Add date range parameters if they exist
  if (start_date) {
    queryParams += `&start_date=${start_date}`;
  }
  if (end_date) {
    queryParams += `&end_date=${end_date}`;
  }

  // Add is_client_uploaded parameter if it exists (boolean or empty string)
  if (is_client_uploaded !== '' && is_client_uploaded !== undefined) {
    queryParams += `&is_client_uploaded=${is_client_uploaded}`;
  }

  // Add esignature_status parameter if it exists
  if (esignature_status) {
    queryParams += `&esignature_status=${esignature_status}`;
  }

  // Add shared_folder parameter only when true (for shared folder navigation)
  if (shared_folder === true) {
    queryParams += `&shared_folder=true`;
  }

  // Add folder_shared and shared_contact_list parameters only when folder is actually shared
  if (folder_shared === true) {
    queryParams += `&folder_shared=true`;

    // Add shared_contact_list parameter if it exists (URL encoded)
    if (shared_contact_list && Array.isArray(shared_contact_list) && shared_contact_list.length > 0) {
      const encodedContactList = encodeURIComponent(JSON.stringify(shared_contact_list));
      queryParams += `&shared_contact_list=${encodedContactList}`;
    }
  }

  return performRequest(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.file}?${queryParams}`,
    null,
    '',
    true,
    exclude_from_loading
  );
};

export const listGroupedFiles = (obj, exclude_from_loading = false) => {
  let { case_id, all_files } = obj;
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestThird(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.listGroupedFiles}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&case_id=${case_id ?? 'undefined'}&all_files=${
      all_files ?? false
    }`,
    null,
    null,
    true,
    exclude_from_loading
  );
};

export const extractEntities = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestThird(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.extractEntities}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`,
    data
  );
};

export const getEntities = (obj) => {
  let { case_id, download_flag } = obj;
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestThird(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.getEntities}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&case_id=${
      case_id ?? 'undefined'
    }&download_flag=${download_flag}`
  );
};

export const createTimeline = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestThird(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.createTimeline}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`,
    data
  );
};

export const getTimeline = (obj) => {
  let { case_id, download_flag, start_date, end_date, category, save_as_pdf, save_as_docx, timezone } = obj;
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestThird(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.getTimeline}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&case_id=${
      case_id ?? 'undefined'
    }&download_flag=${download_flag}&start_date=${start_date || ''}&end_date=${end_date || ''}&category=${category || []}&save_as_pdf=${
      save_as_pdf || 0
    }&save_as_docx=${save_as_docx || 0}&timezone=${timezone || ''}`
  );
};

export const saveTags = (obj) => {
  let { data, is_lead = false, timeEntryData = '' } = obj;
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequestSecond(
    apiEndPoints.methodType.PATCH,
    `${apiEndPoints.fileManager.tagFile}?tenant_id=${tenantId}&tenant_sk=${tenantSk}${is_lead ? `&is_lead=${is_lead}` : ''}`,
    data,
    timeEntryData
  );
};

export const listFileTags = (case_id = '', exclude_from_loading = false) => {
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequestSecond(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.listFileTags}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&case_id=${case_id}`,
    null,
    null,
    true,
    exclude_from_loading
  );
};

export const getFileClients = (obj) => {
  let { case_id = '' } = obj;
  const [tenantId] = getTenantDetails();
  return performRequestSecond(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.fileClients}?tenant_id=${tenantId}&case_id=${case_id}`
  );
};

export const shareFile = (obj) => {
  let { case_id, data, is_lead, timeEntryData = '' } = obj;
  const [tenantId, tenantSk] = getTenantDetails();

  // Always use fileSharing endpoint for both files and folders
  return performRequestSecond(
    apiEndPoints.methodType.PATCH,
    `${apiEndPoints.fileSharing}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&case_id=${case_id}${is_lead ? `&is_lead=${is_lead}` : ''}`,
    data,
    timeEntryData
  );
};

// Keep shareFolder for backward compatibility but redirect to shareFile
export const shareFolder = (obj) => {
  // Simply call shareFile - it now handles both files and folders using the same endpoint
  return shareFile(obj);
};

export const createFile = (obj) => {
  let { data, is_lead = false, isFirmDocuments = false, timeEntryData = '' } = obj;
  const [tenantId, tenantSk] = getTenantDetails();

  let queryParams = `tenant_id=${tenantId}&tenant_sk=${tenantSk}`;
  if (is_lead) {
    queryParams += `&is_lead=${is_lead}`;
  }
  if (isFirmDocuments) {
    queryParams += `&isFirmDocuments=${isFirmDocuments}`;
  }

  return performRequest(apiEndPoints.methodType.POST, `${apiEndPoints.fileManager.file}?${queryParams}`, data, timeEntryData);
};

export const updateFile = (obj) => {
  let { data, is_lead = false, timeEntryData = '' } = obj;
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequest(
    apiEndPoints.methodType.PUT,
    `${apiEndPoints.fileManager.file}?tenant_id=${tenantId}&tenant_sk=${tenantSk}${is_lead ? `&is_lead=${is_lead}` : ''}`,
    data,
    timeEntryData
  );
};

export const getTemplates = (params) => {
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequestSecond(apiEndPoints.methodType.GET, `/templates?tenant_id=${tenantId}&tenant_sk=${tenantSk}&limit=100${params}`);
};

export const createTemplate = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestSecond(apiEndPoints.methodType.POST, `/templates?tenant_id=${tenantId}&tenant_sk=${tenantSk}`, data);
};

export const getFolders = (params) => {
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequest(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.file}/folders?tenant_id=${tenantId}&tenant_sk=${tenantSk}&limit=100&${params}`
  );
};

export const listCasesForClientUsers = (obj) => {
  let { target_user_type, target_user_id, target_user_name, is_lead = true, is_events = false, keyword } = obj;

  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.getUserCases}?tenant_sk=${tenantSk}&tenant_id=${tenantId}&target_user_type=${target_user_type}&target_user_id=${target_user_id}&target_user_name=${target_user_name}&is_lead=${is_lead}&is_events=${is_events}&keyword=${keyword}`;

  return performRequest(apiEndPoints.methodType.GET, url);
};

export const sendESigantureRequest = (obj) => {
  let { case_id, file_pk, file_sk, data, is_lead, timeEntryData = '' } = obj;

  const [tenantId] = getTenantDetails();

  let filePk = file_pk ? file_pk.replaceAll('#', '%23') : '';
  let fileSk = file_sk ? file_sk.replaceAll('#', '%23') : '';
  return performRequestSecond(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.sendESignature}?tenant_id=${tenantId}&case_id=${case_id}&file_pk=${filePk}&file_sk=${fileSk}${
      is_lead ? `&is_lead=${is_lead}` : ''
    }`,
    data,
    timeEntryData
  );
};

export const presignedURLGet = (file_path, version_id = false, is_profile_image = false) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.presignedURLGet}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&file_path=${encodeURI(file_path)}${
    version_id ? `&version_id=${version_id}` : ''
  }${is_profile_image ? `&is_profile_image=${is_profile_image}` : ''}`;
  return performRequestSecond(apiEndPoints.methodType.GET, url);
};

export const deleteBulkFile = (data, operation, is_lead) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.bulkDeleteMoveFile}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&file_operation=${operation}&is_lead=${is_lead}`;
  return performRequestThird(apiEndPoints.methodType.POST, url, data);
};

export const moveBulkFile = (data, operation, is_lead) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.bulkDeleteMoveFile}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&file_operation=${operation}&is_lead=${is_lead}`;
  return performRequestThird(apiEndPoints.methodType.POST, url, data);
};

export const validateUpload = (size) => {
  const [tenantId, tenantSk] = getTenantDetails();
  let url = `${apiEndPoints.validateUpload}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&file_size=${size}`;
  return performRequestThird(apiEndPoints.methodType.GET, url);
};

export const sendESigntureRequestZoho = (obj) => {
  let { case_id, file_pk, file_sk, data, is_lead, timeEntryData = '' } = obj;

  const [tenantId, tenantSk] = getTenantDetails();

  let filePk = file_pk ? file_pk.replaceAll('#', '%23') : '';
  let fileSk = file_sk ? file_sk.replaceAll('#', '%23') : '';
  return performRequestThird(
    apiEndPoints.methodType.POST,
    `${
      apiEndPoints.fileManager.sendESignatureZoho
    }?tenant_id=${tenantId}&tenant_sk=${tenantSk}&case_id=${case_id}&file_pk=${filePk}&file_sk=${fileSk}${
      is_lead ? `&is_lead=${is_lead}` : ''
    }`,
    data,
    timeEntryData
  );
};

export const generateSignUrlZoho = (obj) => {
  let { case_id, file_pk, file_sk, data, is_lead, timeEntryData = '' } = obj;

  const [tenantId] = getTenantDetails();

  let filePk = file_pk ? file_pk.replaceAll('#', '%23') : '';
  let fileSk = file_sk ? file_sk.replaceAll('#', '%23') : '';
  data.host = window.origin;
  return performRequestThird(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.generateSignUrl}?tenant_id=${tenantId}&case_id=${case_id}&file_pk=${filePk}&file_sk=${fileSk}${
      is_lead ? `&is_lead=${is_lead}` : ''
    }`,
    data,
    timeEntryData
  );
};
export const generateSignUrlZohoNoAuth = (obj) => {
  // const [tenantId] = getTenantDetails();

  // let filePk = file_pk ? file_pk.replaceAll('#', '%23') : '';
  // let fileSk = file_sk ? file_sk.replaceAll('#', '%23') : '';
  obj.host = window.origin;
  return performRequestThird(apiEndPoints.methodType.POST, `${apiEndPoints.fileManager.generateSignUrlMail}`, obj, '', false);
};

export const getSignatureStatus = (obj, disableLoading) => {
  let { case_id, file_pk, file_sk, is_lead, timeEntryData = '' } = obj;

  const [tenantId] = getTenantDetails();

  let filePk = file_pk ? file_pk.replaceAll('#', '%23') : '';
  let fileSk = file_sk ? file_sk.replaceAll('#', '%23') : '';
  return performRequestThird(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.getSignatureStatus}?tenant_id=${tenantId}&case_id=${case_id}&file_pk=${filePk}&file_sk=${fileSk}${
      is_lead ? `&is_lead=${is_lead}` : ''
    }`,
    {},
    timeEntryData,
    true,
    disableLoading
  );
};

export const copyFileUrl = (obj) => {
  let { data } = obj;

  const [tenantId, tenantSk] = getTenantDetails();

  return performRequestThird(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.copyFileUrl}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`,
    data
  );
};

export const getSharedFiles = (obj) => {
  let { token, file_system_sk } = obj;
  // const [tenantId, tenantSk] = getTenantDetails();

  return performRequestThird(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.getSharedFiles}?token=${token}${file_system_sk ? `&file_system_sk=${file_system_sk}` : ''}`,
    null,
    null,
    false
  );
};

export const getSharedLinkStats = (obj) => {
  let { file_system_sk, lawft_link_id } = obj;
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequestThird(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.getSharedLinkStats}?tenant_id=${tenantId}&tenant_sk=${tenantSk}${
      file_system_sk ? `&file_system_sk=${file_system_sk}` : ''
    }${lawft_link_id ? `&lawft_link_id=${lawft_link_id}` : ''}`
  );
};

export const summarizeDocument = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequestThird(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.summarizeDocument}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`,
    data
  );
};

export const saveSummary = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return performRequestSecond(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.saveSummary}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`,
    data
  );
};

export const courtDateEvent = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequestThird(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.courtDateEvent}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`,
    data
  );
};

export const detectDiscrepancy = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequestThird(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.detectDiscrepancy}?tenant_id=${tenantId}&tenant_sk=${tenantSk}`,
    data
  );
};

export const getDiscrepancies = (pageLimit, pagination_token = '', pagination_direction = '', sk = '', keyword = '', case_id = '') => {
  const [tenantId, tenantSk] = getTenantDetails();

  return performRequestThird(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.fileManager.getDiscrepancies}?tenant_id=${tenantId}&tenant_sk=${tenantSk}&limit=${pageLimit}&pagination_token=${pagination_token}&pagination_direction=${pagination_direction}&sk=${sk}&keyword=${keyword}&case_id=${case_id}`
  );
};

export const markBulkFilesAsRead = (data) => {
  const [tenantId] = getTenantDetails();
  let url = `${apiEndPoints.fileManager.fileReadStatus}?tenant_id=${tenantId}`;
  return performRequestThird(apiEndPoints.methodType.POST, url, data);
};

export const recallSignatureRequest = (obj) => {
  let { file_pk, file_sk, timeEntryData = '' } = obj;
  const [tenantId] = getTenantDetails();

  let filePk = file_pk ? file_pk.replaceAll('#', '%23') : '';
  let fileSk = file_sk ? file_sk.replaceAll('#', '%23') : '';

  return performRequestThird(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.fileManager.recallSignature}?tenant_id=${tenantId}`,
    obj,
    timeEntryData
  );
};
